import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash2, ExternalLink, Check, Clock, UserPlus } from "lucide-react";

export default function AdminOnboarding() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", plan: "pro", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/onboarding-invites");
      setInvites(r.data || []);
    } catch (e) {
      toast.error("Could not load invites");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/admin/onboarding-invites", form);
      toast.success("Invite created — copy the link");
      setInvites((cur) => [r.data, ...cur.filter((i) => i.id !== r.data.id)]);
      setOpenNew(false);
      setForm({ client_name: "", client_email: "", plan: "pro", note: "" });
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Invite URL copied");
  };

  const del = async (id) => {
    if (!confirm("Delete this invite?")) return;
    await api.delete(`/admin/onboarding-invites/${id}`);
    setInvites((cur) => cur.filter((i) => i.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="space-y-6" data-testid="admin-onboarding-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="vp-label">Founder tools</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1">Client onboarding invites</h1>
          <p className="text-[#4A4D57] text-sm mt-2 max-w-xl">
            Ek shareable link banao, client bharega form aur seedha uska Videoepreneur workspace ready ho jaayega — brand kit + payment + first webinar sab configured.
          </p>
        </div>
        <button
          onClick={() => setOpenNew((v) => !v)}
          data-testid="admin-onboarding-new-btn"
          className="vp-btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={2.25} /> New invite
        </button>
      </div>

      {openNew && (
        <form onSubmit={create} className="vp-glass p-6 space-y-4 vp-fade-up" data-testid="admin-onboarding-new-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Client name</span>
              <input required className="vp-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Kamlesh Sharma" data-testid="ob-invite-name" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Client email</span>
              <input required type="email" className="vp-input" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="you@brand.com" data-testid="ob-invite-email" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Plan to apply</span>
              <select className="vp-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Note (private)</span>
              <input className="vp-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Referral from XYZ" />
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpenNew(false)} className="vp-btn-ghost">Cancel</button>
            <button type="submit" disabled={creating} className="vp-btn-primary" data-testid="admin-onboarding-create-submit">
              {creating ? "Creating…" : "Create invite"}
            </button>
          </div>
        </form>
      )}

      <div className="vp-glass overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest font-bold text-[#747680] border-b border-[#E4E6EC]">
          <div className="col-span-4">Client</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Invite URL</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading && <div className="px-5 py-10 text-center text-[#747680]">Loading…</div>}
        {!loading && invites.length === 0 && (
          <div className="px-5 py-12 text-center">
            <UserPlus className="w-8 h-8 mx-auto text-[#C8CCD6]" strokeWidth={1.5} />
            <div className="text-sm text-[#4A4D57] mt-3">No invites yet — create your first one above.</div>
          </div>
        )}
        {invites.map((i) => (
          <div key={i.id} className="grid grid-cols-12 px-5 py-4 items-center hover:bg-[#F6F7FA] border-b border-[#E4E6EC] last:border-b-0" data-testid={`invite-row-${i.id}`}>
            <div className="col-span-4 min-w-0">
              <div className="font-semibold text-[#0B0D17] truncate">{i.client_name}</div>
              <div className="text-xs text-[#747680] truncate">{i.client_email}</div>
            </div>
            <div className="col-span-2">
              <span className="px-2 py-0.5 rounded-full bg-[#0B5CFF]/10 text-[#0B5CFF] text-[11px] font-bold uppercase">{i.plan || "free"}</span>
            </div>
            <div className="col-span-2">
              {i.status === "completed" ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#1E8E3E]">
                  <Check className="w-3.5 h-3.5" /> Completed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#F59E0B]">
                  <Clock className="w-3.5 h-3.5" /> Pending
                </span>
              )}
            </div>
            <div className="col-span-3 min-w-0">
              <div className="text-xs text-[#4A4D57] truncate font-mono">{i.invite_url}</div>
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              <button onClick={() => copyUrl(i.invite_url)} title="Copy URL" className="p-2 rounded-lg hover:bg-[#E4E6EC] text-[#4A4D57]">
                <Copy className="w-4 h-4" />
              </button>
              <a href={i.invite_url} target="_blank" rel="noreferrer" title="Open" className="p-2 rounded-lg hover:bg-[#E4E6EC] text-[#4A4D57]">
                <ExternalLink className="w-4 h-4" />
              </a>
              <button onClick={() => del(i.id)} title="Delete" className="p-2 rounded-lg hover:bg-red-100 text-red-600" data-testid={`invite-delete-${i.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
